import React from 'react';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import greenTick from '../../../../assets/green-tick.svg';
import NotCheckedIcon from '../../../RiskIndicators/NotCheckedIcon';
import PotentialRiskRedirectIcon from '../../../RiskIndicators/PotentialRiskRedirectIcon';
import type { LegalCasesRiskState } from '../../../RiskIndicators/RiskIndicators';
import { RISK_STATE_CONFIG, RegulatoryRiskState, WRAPPING_TOOLTIP_CSS } from '../../../RiskIndicators/RiskIndicatorConstants';
import { NOT_CONFIGURED_LABEL, NOT_CONFIGURED_TOOLTIP } from './ManagementProfileTabConstants';
import styles from './ManagementProfileTab.module.scss';

// The non-clickable check states shared by the Management Profile table cells and the director drawer.

/** Grey minus-circle + "Not Configured": the PNO's config does not run this check for this director. */
export const NotConfiguredLabel = () => (
    <span className={styles.notConfigured}>
        <NotCheckedIcon className={styles.notConfiguredIcon} color="currentColor" />
        {NOT_CONFIGURED_LABEL}
    </span>
);

const NOT_CHECKED = RISK_STATE_CONFIG[RegulatoryRiskState.NOT_CHECKED];

/** Minus-circle + "Not Checked", in the Legal & Regulatory table's own label (colour: --risk-indicator-notChecked). */
export const NotCheckedLabel = () => (
    <span className={styles.notCheckedLabel}>
        <NotCheckedIcon className={styles.notConfiguredIcon} color="currentColor" />
        {NOT_CHECKED.label}
    </span>
);

/** Light-grey minus-circle + "No Data": same look as Not Configured, as in the design. */
export const NoDataLabel = () => (
    <span className={styles.notConfigured}>
        <NotCheckedIcon className={styles.notConfiguredIcon} color="currentColor" />
        {RISK_STATE_CONFIG[RegulatoryRiskState.NOT_AVAILABLE].label}
    </span>
);

/** A hover (and keyboard-focus) explanation around a cell's content. */
export const WithHoverTip = ({
    tooltip,
    focusable = true,
    children,
}: {
    tooltip: string;
    focusable?: boolean;
    children: React.ReactNode;
}) => (
    <Tooltip content={tooltip} direction="top" contentCss={WRAPPING_TOOLTIP_CSS} fitContent small usePortal focusable={focusable}>
        {children}
    </Tooltip>
);

/** Table cell: Not Configured with its reason. Never clickable. */
export const NotConfiguredCell = () => (
    <WithHoverTip tooltip={NOT_CONFIGURED_TOOLTIP}>
        <NotConfiguredLabel />
    </WithHoverTip>
);

/**
 * A Legal Cases state as the Legal & Regulatory table draws it: the label in its state colour with
 * the tick or redirect arrow, and the disposed-case sub-label under it (table) or beside it (drawer).
 */
export const LegalCasesStateLabel = ({
    state,
    inline = false,
    showIcon = true,
}: {
    state: LegalCasesRiskState;
    inline?: boolean;
    showIcon?: boolean;
}) => (
    <span
        className={inline ? styles.legalStateInline : styles.legalStateStacked}
        // Hover tint follows the state: red for Confirmed, amber for Probable, neutral grey for a
        // green "No Open" (the shared pink fallback is a red-alert colour).
        style={
            {
                ['--badge-hover-bg' as string]: state.tick ? 'var(--hover-three-dot-bg)' : state.backgroundColor,
            } as React.CSSProperties
        }
    >
        <span className={styles.alertBadge} style={{ color: state.color }}>
            {showIcon && state.tick && <img src={greenTick} alt="" className={styles.CountIcon} />}
            {showIcon && state.id === 'NOT_AVAILABLE' && <NotCheckedIcon className={styles.notConfiguredIcon} color="currentColor" />}
            {showIcon && state.openable && !state.tick && (
                <PotentialRiskRedirectIcon className={styles.regulatoryBadgeIcon} color={state.color} />
            )}
            {state.label}
        </span>
        {state.subLabel && <span className={styles.legalSubLabel}>{state.subLabel}</span>}
    </span>
);
